<script setup>
import { computed, ref } from 'vue'
import { router } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { countyModeKey } from '@/constants/countyAccess'

const props = defineProps({
  user: { type: Object, required: true },
  // One row per (county, mode) — [{county, mode, company_default, override, effective, qualified}]
  // from CountyAccess::matrixFor; mode null = the county has no model coverage,
  // qualified = the mode is served to qualifying subjects only.
  countyAccess: { type: Array, default: () => [] },
})

const { t } = useI18n()
const toast = useToast()

// Which (county, mode) row is currently saving (disables just that row's control).
const savingKey = ref(null)

const rows = computed(() =>
  props.countyAccess.map((row) => ({ ...row, key: countyModeKey(row.county, row.mode) })),
)

const enabledCount = computed(() => props.countyAccess.filter((row) => row.effective).length)

const overrideOptions = computed(() => [
  { label: t('messages.admin.countyAccess.inherit'), value: 'inherit' },
  { label: t('messages.admin.countyAccess.grant'), value: 'grant' },
  { label: t('messages.admin.countyAccess.revoke'), value: 'revoke' },
])

const stateFor = (row) => {
  if (row.override === null || row.override === undefined) return 'inherit'
  return row.override ? 'grant' : 'revoke'
}

const modeLabel = (mode) => {
  if (mode === 'comparables') return t('messages.admin.countyAccess.modeComparables')
  if (mode === 'direct') return t('messages.admin.countyAccess.modeDirect')
  return t('messages.admin.countyAccess.modeNone')
}

const modeSeverity = (mode) => {
  if (mode === 'comparables') return 'info'
  if (mode === 'direct') return 'success'
  return 'secondary'
}

const onStateChange = (row, state) => {
  if (state === null || row.mode === null || state === stateFor(row)) return

  savingKey.value = row.key
  router.put(
    route('admin.user.county-access', props.user.id),
    { county: row.county, mode: row.mode, state },
    {
      preserveScroll: true,
      onSuccess: () => {
        toast.add({
          severity: 'success',
          summary: t('messages.updated'),
          detail: t('messages.admin.countyAccess.updated'),
          life: 3000,
        })
      },
      onFinish: () => {
        savingKey.value = null
      },
    },
  )
}
</script>

<template>
  <Card>
    <template #title>
      <div class="flex items-center justify-between">
        <span>{{ $t('messages.admin.user.tabs.counties') }}</span>
        <span class="text-sm font-normal text-gray-500">
          {{ $t('messages.admin.countyAccess.enabledCount', { count: enabledCount, total: countyAccess.length }) }}
        </span>
      </div>
    </template>
    <template #content>
      <p class="text-sm text-gray-600 mb-4">
        {{ $t('messages.admin.countyAccess.userHint') }}
      </p>
      <DataTable
        :value="rows"
        data-key="key"
        size="small"
        striped-rows
        row-group-mode="rowspan"
        group-rows-by="county"
        data-testid="admin-county-matrix"
      >
        <Column field="county" :header="$t('messages.admin.countyAccess.county')">
          <template #body="{ data }">
            <span :class="data.mode === null ? 'text-gray-400' : 'text-gray-800'">
              {{ data.county }}
            </span>
          </template>
        </Column>
        <Column field="mode" :header="$t('messages.admin.countyAccess.mode')">
          <template #body="{ data }">
            <span class="inline-flex items-center gap-1">
              <Tag :value="modeLabel(data.mode)" :severity="modeSeverity(data.mode)" class="text-xs!" />
              <span
                v-if="data.qualified"
                v-tooltip.top="$t('messages.admin.countyAccess.qualifiedHint')"
                class="text-xs text-gray-500 cursor-help"
                data-testid="county-mode-qualified"
              >
                ({{ $t('messages.admin.countyAccess.qualified') }})
              </span>
            </span>
          </template>
        </Column>
        <Column field="company_default" :header="$t('messages.admin.countyAccess.companyDefault')">
          <template #body="{ data }">
            <span
              :class="data.company_default ? 'pi pi-check text-emerald-500' : 'pi pi-minus text-gray-300'"
            ></span>
          </template>
        </Column>
        <Column :header="$t('messages.admin.countyAccess.override')">
          <template #body="{ data }">
            <SelectButton
              :model-value="stateFor(data)"
              :options="overrideOptions"
              option-label="label"
              option-value="value"
              size="small"
              :allow-empty="false"
              :disabled="savingKey !== null || data.mode === null"
              :loading="savingKey === data.key"
              @update:model-value="onStateChange(data, $event)"
            />
          </template>
        </Column>
        <Column :header="$t('messages.admin.countyAccess.effective')">
          <template #body="{ data }">
            <Tag
              :value="data.effective
                ? $t('messages.admin.countyAccess.enabled')
                : $t('messages.admin.countyAccess.disabled')"
              :severity="data.effective ? 'success' : 'secondary'"
              class="text-xs!"
            />
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>
</template>
